import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from '@/lib/api';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
const LANGUAGES = [
  { code: "pt", label: "PT", flag: "🇵🇹", flagAlt: "PT" },
  { code: "en", label: "EN", flag: "🇬🇧", flagAlt: "GB" },
  { code: "es", label: "ES", flag: "🇪🇸", flagAlt: "ES" },
  { code: "fr", label: "FR", flag: "🇫🇷", flagAlt: "FR" },
];

/** Simple deterministic hash — matches Python _text_hash() in mutations.py */
function textHash(s) {
  if (!s) return "";
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) & 0xFFFFFFFF;
  }
  return (h >>> 0).toString(16);
}

/**
 * TranslatableField - Input or Textarea with language tabs and auto-translate
 * 
 * @param {string} id - Field identifier
 * @param {string} label - Field label
 * @param {object} value - Object with translations { pt: "", en: "", es: "", fr: "" }
 * @param {function} onChange - Callback when any translation changes
 * @param {string} placeholder - Placeholder text
 * @param {boolean} required - If field is required
 * @param {boolean} multiline - If true, uses Textarea instead of Input
 * @param {number} rows - Number of rows for Textarea (default: 3)
 * @param {number} maxLength - Maximum character length (optional)
 * @param {string} className - Additional CSS classes
 * @param {string} activeTab - Externally controlled active tab (optional)
 * @param {function} onTabChange - Callback when tab changes (optional)
 */
export function TranslatableField({
  id,
  label,
  value = {},
  onChange,
  placeholder = "",
  required = false,
  multiline = false,
  rows = 3,
  maxLength,
  className = "",
  activeTab: externalActiveTab,
  onTabChange,
  stretch = false,
}) {
  const { t, i18n } = useTranslation("common");
  const currentLang = i18n.language?.split("-")[0] || "pt";
  const [internalActiveTab, setInternalActiveTab] = useState(currentLang);
  
  // Use external tab if provided, otherwise use internal
  const activeTab = externalActiveTab ?? internalActiveTab;
  
  // Sync tab with language switcher changes
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      const newLang = lng?.split("-")[0] || "pt";
      if (onTabChange) {
        onTabChange(newLang);
      } else {
        setInternalActiveTab(newLang);
      }
    };
    
    i18n.on("languageChanged", handleLanguageChange);
    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n, onTabChange]);
  
  // Handle tab change
  const handleTabChange = (tab) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };
  
  // Get value for a specific language
  const getValue = (lang) => {
    if (typeof value === "string") {
      return lang === "pt" ? value : "";
    }
    return value[lang] || "";
  };
  
  // Update a specific language translation
  const updateTranslation = (lang, newValue) => {
    const newTranslations = typeof value === "string" 
      ? { pt: value, en: "", es: "", fr: "", [lang]: newValue || "" }
      : { ...value, [lang]: newValue || "" };

    // Always mark the edited language as the source language
    // so the backend knows which language changed and can re-translate the others
    newTranslations._src_lang = lang;

    // If primary/source language text is cleared, clear all translations
    if (lang === currentLang && !(newValue || "").trim()) {
      for (const l of LANGUAGES) {
        if (l.code !== lang) newTranslations[l.code] = "";
      }
    }

    onChange(newTranslations);
  };
  
  const InputComponent = multiline ? Textarea : Input;
  
  // Order languages: current user language first, then others
  const orderedLanguages = [
    LANGUAGES.find(l => l.code === currentLang),
    ...LANGUAGES.filter(l => l.code !== currentLang)
  ].filter(Boolean);
  
  return (
    <div className={stretch ? `flex flex-col gap-2 lg:grid lg:grid-rows-subgrid lg:row-span-3 ${className}` : `space-y-2 ${className}`}>
      {/* Label */}
      <Label htmlFor={`${id}-${activeTab}`}>
        {label} {required && activeTab === currentLang && "*"}
      </Label>
      
      {/* Language Tabs + Translate Button - all together */}
      <div className="flex items-center gap-1 sm:gap-2">
        <div className="flex gap-0.5 sm:gap-1">
          {orderedLanguages.map((lang) => {
            const isActive = activeTab === lang.code;
            const isPrimary = lang.code === currentLang;
            const langValue = getValue(lang.code);
            const isEmpty = !langValue?.trim?.();

            // Red ring if: (a) empty (required fields, or partial fill), or
            // (b) source language changed (_src_lang is set and this is a secondary tab)
            // (c) legacy: _src_hash mismatch
            // Exception: optional field with ALL languages empty → no border
            let needsTranslation = isEmpty;
            if (needsTranslation && !required) {
              // Optional field: only show border if at least one language has content
              const allEmpty = LANGUAGES.every(l => !getValue(l.code)?.trim?.());
              if (allEmpty) needsTranslation = false;
            }
            if (!needsTranslation && !isEmpty && typeof value === "object") {
              // Check _src_lang: user edited a language, secondary tabs are stale
              if (value._src_lang && lang.code !== value._src_lang) {
                needsTranslation = true;
              }
              // Legacy check: _src_hash mismatch
              if (!needsTranslation && value._src_hash) {
                const srcLang = value._src_lang || "pt";
                if (lang.code !== srcLang) {
                  const currentSrcText = getValue(srcLang);
                  if (currentSrcText?.trim?.() && textHash(currentSrcText.trim()) !== value._src_hash) {
                    needsTranslation = true;
                  }
                }
              }
            }
            
            return (
              <button
                key={`${id}-tab-${lang.code}`}
                data-testid={`${id}-lang-${lang.code}`}
                type="button"
                onClick={() => handleTabChange(lang.code)}
                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  isActive 
                    ? "bg-primary text-primary-foreground" 
                    : needsTranslation
                      ? "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted ring-1 ring-destructive/60"
                      : "bg-muted/50 text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                <img loading="lazy" 
                  src={`https://flagcdn.com/16x12/${lang.code === 'en' ? 'gb' : lang.code}.png`}
                  srcSet={`https://flagcdn.com/32x24/${lang.code === 'en' ? 'gb' : lang.code}.png 2x`}
                  alt={lang.flagAlt}
                  className="w-4 h-3 flex-shrink-0"
                />
                <span className={isActive ? "" : "hidden sm:inline"}>{lang.label}</span>
                {isPrimary && (
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-primary-foreground" : "bg-primary"}`} />
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Input field for active language — secondary languages are read-only */}
      {(() => {
        const isSecondary = activeTab !== currentLang;
        const currentLen = (getValue(activeTab) || "").length;
        return stretch ? (
          <div className="min-h-[200px] [&>.relative]:h-full [&>.relative>textarea]:h-full [&>textarea]:h-full">
            <InputComponent
              id={`${id}-${activeTab}`}
              value={getValue(activeTab)}
              onChange={isSecondary ? undefined : (e) => updateTranslation(activeTab, maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
              readOnly={isSecondary}
              placeholder={isSecondary ? t("translations.auto_filled", "Preenchido automaticamente") : `${placeholder} (${activeTab.toUpperCase()})`}
              autoComplete="off"
              className={`resize-none h-full ${isSecondary ? "opacity-70 cursor-default" : ""}`}
            />
          </div>
        ) : (
          <>
            <InputComponent
              id={`${id}-${activeTab}`}
              value={getValue(activeTab)}
              onChange={isSecondary ? undefined : (e) => updateTranslation(activeTab, maxLength ? e.target.value.slice(0, maxLength) : e.target.value)}
              readOnly={isSecondary}
              placeholder={isSecondary ? t("translations.auto_filled", "Preenchido automaticamente") : `${placeholder} (${activeTab.toUpperCase()})`}
              rows={multiline ? rows : undefined}
              autoComplete="off"
              className={isSecondary ? "opacity-70 cursor-default" : ""}
            />
            {maxLength && !isSecondary && (
              <p className={`text-xs text-right mt-1 ${currentLen >= maxLength ? "text-destructive" : "text-muted-foreground"}`}>
                {currentLen}/{maxLength}
              </p>
            )}
          </>
        );
      })()}
    </div>
  );
}

/**
 * Helper function to get translated value from a translatable field
 * @param {object|string} value - The translatable value object or legacy string
 * @param {string} lang - Target language code
 * @returns {string} The translated value or fallback
 */
export function getTranslatedValue(value, lang = "pt") {
  if (!value) return "";
  if (typeof value === "string") {
    // Guard: ignore bare language codes stored by mistake (e.g. "pt", "en")
    if (value.length <= 3 && /^[a-z]{2,3}$/.test(value)) return "";
    return value;
  }
  // Object: extract value for language, with guard for bare lang codes as values
  const raw = value[lang] || value.pt || Object.values(value).find(v => typeof v === "string" && v?.trim() && v !== "_src_lang") || "";
  if (typeof raw === "string" && raw.length <= 3 && /^[a-z]{2,3}$/.test(raw)) return "";
  return raw;
}

/**
 * Check if a translatable field needs auto-translation
 * Returns true if the field has content in at least one language but missing others
 * @param {object|string} value - The translatable value
 * @returns {boolean}
 */
export function needsAutoTranslation(value) {
  if (!value || typeof value === "string") return false;
  
  const allLangs = ["pt", "en", "es", "fr"];
  
  const filledLangs = allLangs.filter(lang => value[lang]?.trim?.());
  const emptyLangs = allLangs.filter(lang => !value[lang]?.trim?.());
  
  // Has content in at least one language but not all → needs translation
  if (filledLangs.length > 0 && emptyLangs.length > 0) return true;
  
  // Source language was edited (_src_lang set) → re-translate others even if filled
  if (filledLangs.length > 0 && value._src_lang) return true;
  
  return false;
}

/**
 * Get the source language and text for auto-translation
 * Prefers the user's current language, falls back to any filled language
 * @param {object} value - The translatable value object
 * @param {string} userLang - User's current language
 * @returns {{ sourceLang: string, text: string, targetLangs: string[] } | null}
 */
export function getAutoTranslationParams(value, userLang = "pt") {
  if (!value || typeof value === "string") return null;
  
  const allLangs = ["pt", "en", "es", "fr"];
  
  // If _src_lang is set, the user edited that language → re-translate ALL others
  if (value._src_lang && value[value._src_lang]?.trim?.()) {
    const targetLangs = allLangs.filter(l => l !== value._src_lang);
    if (targetLangs.length > 0) {
      return { sourceLang: value._src_lang, text: value[value._src_lang], targetLangs };
    }
  }
  
  // Prefer user's language as source (for initial translations of empty fields)
  if (value[userLang]?.trim?.()) {
    const targetLangs = allLangs.filter(l => l !== userLang && !value[l]?.trim?.());
    if (targetLangs.length > 0) {
      return { sourceLang: userLang, text: value[userLang], targetLangs };
    }
  }
  
  // Fall back to any filled language
  for (const lang of allLangs) {
    if (value[lang]?.trim?.()) {
      const targetLangs = allLangs.filter(l => l !== lang && !value[l]?.trim?.());
      if (targetLangs.length > 0) {
        return { sourceLang: lang, text: value[lang], targetLangs };
      }
    }
  }
  
  return null;
}

/**
 * Auto-translate a single translatable field if needed
 * @param {object} value - The translatable value object
 * @param {string} userLang - User's current language
 * @returns {Promise<object>} - Updated translations object
 */
export async function autoTranslateField(value, userLang = "pt") {
  const params = getAutoTranslationParams(value, userLang);
  if (!params) return value;
  try {
    const response = await api.post("/api/translate", {
      text: params.text,
      source_lang: params.sourceLang,
      target_langs: params.targetLangs
    });
    
    const result = {
      ...value,
      ...response.data.translations
    };
    // Clear _src_lang after successful translation — translations are now in sync
    delete result._src_lang;
    return result;
  } catch (error) {
    console.error("Auto-translation error:", error);
    return value;
  }
}

/**
 * Auto-translate multiple translatable fields before saving
 * @param {object} fields - Object with field names as keys and translatable values
 * @param {string} userLang - User's current language
 * @returns {Promise<object>} - Object with updated translations
 */
export async function autoTranslateFields(fields, userLang = "pt") {
  const result = { ...fields };
  const fieldsToTranslate = [];
  
  // Find fields that need translation
  for (const [fieldName, value] of Object.entries(fields)) {
    if (needsAutoTranslation(value)) {
      fieldsToTranslate.push({ fieldName, value });
    }
  }
  
  if (fieldsToTranslate.length === 0) return result;
  
  // Translate all fields that need it
  for (const { fieldName, value } of fieldsToTranslate) {
    result[fieldName] = await autoTranslateField(value, userLang);
  }
  
  return result;
}

